import {
    INCREMENT_CATEGORY,
    DECREMENT_CATEGORY,
    INCREMENT_BUNDLE_MEMBER,
    DECREMENT_BUNDLE_MEMBER,
    SET_PRICING_MODEL,
} from "../actions/types";

// Default initial state - will be populated when pricing model is set
const initialState = {
    categories: {},
    total: 0,
    pricingModelId: null,
};

// A composition entry is VARIABLE when it has no fixed `count` (it uses min/max).
const isVariableEntry = (entry) => entry.count == null;

// A bundle is flexible when at least one of its composition seats is variable.
// Flexible bundles are limited to a single unit so the variable counts are
// unambiguous.
export const isFlexibleBundle = (category) =>
    !!category.composition && category.composition.some(isVariableEntry);

// Seats consumed by one category given its current selection.
function seatsForCategory(category) {
    if (!category.composition) {
        return category.count;
    }
    if (category.count <= 0) {
        return 0;
    }
    let fixedPerUnit = 0;
    let variableSeats = 0;
    category.composition.forEach((entry) => {
        if (isVariableEntry(entry)) {
            variableSeats += category.variableCounts?.[entry.type] ?? 0;
        } else {
            fixedPerUnit += entry.count;
        }
    });
    // Flexible bundles are capped at one unit, so the variable counts apply once.
    return category.count * fixedPerUnit + variableSeats;
}

// Total seats across all categories. `total` gates seat selection
// (seats.length === travelers.total), so it must reflect real seat usage.
function computeTotal(categories) {
    return Object.values(categories).reduce(
        (sum, category) => sum + seatsForCategory(category),
        0
    );
}

// Max allowed for a variable bundle member, respecting its own `max` and the
// bundle's overall seat cap (seats already used by the fixed part).
export function maxForVariableMember(category, entry) {
    const fixedSeats = category.composition
        .filter((e) => !isVariableEntry(e))
        .reduce((sum, e) => sum + e.count, 0);
    const otherVariable = category.composition
        .filter((e) => isVariableEntry(e) && e.type !== entry.type)
        .reduce((sum, e) => sum + (category.variableCounts?.[e.type] ?? 0), 0);
    const capBySeats = (category.seats ?? 1) - fixedSeats - otherVariable;
    const capByEntry = entry.max != null ? entry.max : capBySeats;
    return Math.min(capByEntry, capBySeats);
}

/**
 * Initialize categories from pricing model
 * @param {Object} pricingModel - The pricing model with categories
 * @returns {Object} The initialized categories state
 */
function initializeCategoriesFromModel(pricingModel) {
    if (!pricingModel || !pricingModel.categories) {
        return {};
    }

    const categories = {};
    Object.keys(pricingModel.categories).forEach((key) => {
        const category = pricingModel.categories[key];
        const composition = category.composition ?? null;

        // Track the variable part of a bundle (e.g. number of children), seeded
        // to each entry's minimum.
        const variableCounts = {};
        if (composition) {
            composition.forEach((entry) => {
                if (isVariableEntry(entry)) {
                    variableCounts[entry.type] = entry.min ?? 0;
                }
            });
        }

        categories[key] = {
            name: category.name,
            description: category.description,
            count: 0,
            // Bundle support: a unit may consume more than one seat and expand
            // into multiple passengers per `composition`.
            seats: category.seats ?? 1,
            composition,
            variableCounts,
        };
    });

    return categories;
}

function travelerReducer(state = initialState, action) {
    switch(action.type) {
        case SET_PRICING_MODEL: {
            const pricingModel = action.payload;
            return {
                ...state,
                categories: initializeCategoriesFromModel(pricingModel),
                total: 0,
                pricingModelId: pricingModel?.id || null,
            };
        }

        case INCREMENT_CATEGORY: {
            const category = action.payload;
            const current = state.categories[category];
            if (!current) {
                return state;
            }
            // Flexible bundles act as a single unit (toggle); their variable
            // members are chosen separately.
            const maxCount = isFlexibleBundle(current) ? 1 : Infinity;
            if (current.count >= maxCount) {
                return state;
            }
            const categories = {
                ...state.categories,
                [category]: { ...current, count: current.count + 1 },
            };
            return { ...state, categories, total: computeTotal(categories) };
        }

        case DECREMENT_CATEGORY: {
            const category = action.payload;
            const current = state.categories[category];
            if (!current) {
                return state;
            }
            const newCount = Math.max(0, current.count - 1);
            // Reset the variable part when the bundle is removed.
            const variableCounts =
                newCount === 0 && current.composition
                    ? Object.fromEntries(
                          Object.keys(current.variableCounts ?? {}).map((k) => [
                              k,
                              current.composition.find((e) => e.type === k)?.min ?? 0,
                          ])
                      )
                    : current.variableCounts;
            const categories = {
                ...state.categories,
                [category]: { ...current, count: newCount, variableCounts },
            };
            return { ...state, categories, total: computeTotal(categories) };
        }

        case INCREMENT_BUNDLE_MEMBER: {
            const { category, type } = action.payload;
            const current = state.categories[category];
            if (!current || !current.composition) {
                return state;
            }
            const entry = current.composition.find(
                (e) => e.type === type && isVariableEntry(e)
            );
            if (!entry) {
                return state;
            }
            const value = current.variableCounts?.[type] ?? 0;
            if (value >= maxForVariableMember(current, entry)) {
                return state;
            }
            const categories = {
                ...state.categories,
                [category]: {
                    ...current,
                    variableCounts: { ...current.variableCounts, [type]: value + 1 },
                },
            };
            return { ...state, categories, total: computeTotal(categories) };
        }

        case DECREMENT_BUNDLE_MEMBER: {
            const { category, type } = action.payload;
            const current = state.categories[category];
            if (!current || !current.composition) {
                return state;
            }
            const entry = current.composition.find(
                (e) => e.type === type && isVariableEntry(e)
            );
            if (!entry) {
                return state;
            }
            const value = current.variableCounts?.[type] ?? 0;
            const min = entry.min ?? 0;
            if (value <= min) {
                return state;
            }
            const categories = {
                ...state.categories,
                [category]: {
                    ...current,
                    variableCounts: { ...current.variableCounts, [type]: value - 1 },
                },
            };
            return { ...state, categories, total: computeTotal(categories) };
        }

        default:
            return state;
    }
}

export default travelerReducer;
